import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sv } from 'date-fns/locale';
import QuoteItems from './QuoteItems';
import config from '../../config/config';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';

interface QuoteItem {
  articleNumber?: string;
  description: string;
  quantity: number;
  price: number;
  discount?: {
    type: 'percentage' | 'amount';
    value: number;
    description?: string;
  };
  isRot?: boolean;
  isRut?: boolean;
}

interface Quote {
  customerName: string;
  customerEmail: string;
  customerAddress: string;
  customerPostalCode: string;
  customerCity: string;
  customerPersonalNumber?: string;
  deliveryAddress?: string;
  deliveryPostalCode?: string;
  deliveryCity?: string;
  dueDate: Date;
  items: QuoteItem[];
  propertyDesignation?: string;
  housingAssociationNumber?: string;
  apartmentNumber?: string;
  workPeriodStart?: Date;
  workPeriodEnd?: Date;
  workAddress?: string;
  workDescription?: string;
  status: 'pending' | 'accepted' | 'rejected';
  validUntil: Date;
  materialCost?: number;
  otherCosts?: number;
}

interface CreateQuoteProps {
  onClose?: () => void;
  onCreated?: (message: string, severity?: 'success' | 'error' | 'info') => void;
}

const CreateQuote: React.FC<CreateQuoteProps> = ({ onClose, onCreated }) => {
  const [quote, setQuote] = useState<Quote>({
    customerName: '',
    customerEmail: '',
    customerAddress: '',
    customerPostalCode: '',
    customerCity: '',
    dueDate: new Date(),
    items: [],
    status: 'pending',
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handlePreview = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.api.baseUrl}/quotes/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_name: quote.customerName,
          customer_email: quote.customerEmail,
          customer_address: quote.customerAddress,
          customer_postal_code: quote.customerPostalCode,
          customer_city: quote.customerCity,
          customer_personal_number: quote.customerPersonalNumber,
          delivery_address: quote.deliveryAddress,
          delivery_postal_code: quote.deliveryPostalCode,
          delivery_city: quote.deliveryCity,
          property_designation: quote.propertyDesignation,
          housing_association_number: quote.housingAssociationNumber,
          apartment_number: quote.apartmentNumber,
          work_period_start: quote.workPeriodStart,
          work_period_end: quote.workPeriodEnd,
          work_address: quote.workAddress,
          work_description: quote.workDescription,
          due_date: quote.dueDate,
          valid_until: quote.validUntil,
          items: quote.items
        })
      });

      if (!response.ok) {
        throw new Error('Kunde inte generera förhandsgranskning');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewOpen(true);
      setLoading(false);
    } catch (error) {
      if (onCreated) onCreated('Ett fel uppstod när förhandsgranskningen skulle genereras', 'error');
      setLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Formatera datumen till YYYY-MM-DD
      const formatDate = (date: Date) => {
        return date.toISOString().split('T')[0];
      };

      // Beräkna totalt belopp
      const calculateTotal = () => {
        let total = 0;
        
        // Lägg till summan av alla artiklar
        quote.items.forEach(item => {
          let itemTotal = item.quantity * item.price;
          if (item.discount) {
            if (item.discount.type === 'percentage') {
              itemTotal = itemTotal * (1 - item.discount.value / 100);
            } else {
              itemTotal = itemTotal - item.discount.value;
            }
          }
          total += itemTotal;
        });

        return total;
      };

      const response = await fetch(`${config.api.baseUrl}/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_name: quote.customerName,
          customer_email: quote.customerEmail,
          customer_address: quote.customerAddress,
          customer_postal_code: quote.customerPostalCode,
          customer_city: quote.customerCity,
          customer_personal_number: quote.customerPersonalNumber,
          delivery_address: quote.deliveryAddress,
          delivery_postal_code: quote.deliveryPostalCode,
          delivery_city: quote.deliveryCity,
          property_designation: quote.propertyDesignation,
          housing_association_number: quote.housingAssociationNumber,
          apartment_number: quote.apartmentNumber,
          work_period_start: quote.workPeriodStart ? formatDate(quote.workPeriodStart) : null,
          work_period_end: quote.workPeriodEnd ? formatDate(quote.workPeriodEnd) : null,
          work_address: quote.workAddress,
          work_description: quote.workDescription,
          due_date: formatDate(quote.dueDate),
          valid_until: formatDate(quote.validUntil),
          items: quote.items,
          status: quote.status,
          total_amount: calculateTotal()
        })
      });

      if (!response.ok) {
        if (onCreated) onCreated('Kunde inte skapa offerten', 'error');
        setLoading(false);
        return;
      }

      if (onCreated) onCreated('Offerten skapades och skickades till kund', 'success');

      // Återställ formuläret
      setQuote({
        customerName: '',
        customerEmail: '',
        customerAddress: '',
        customerPostalCode: '',
        customerCity: '',
        dueDate: new Date(),
        items: [],
        status: 'pending',
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      });

      // Stäng dialogen om onClose finns
      if (onClose) {
        onClose();
      }
    } catch (error) {
      if (onCreated) onCreated('Ett fel uppstod när offerten skulle skapas', 'error');
      setLoading(false);
    }
    setLoading(false);
  };

  const handleItemsChange = (items: QuoteItem[]) => {
    setQuote({ ...quote, items });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Kundinformation
                </Typography>
                <TextField
                  fullWidth
                  label="Namn"
                  value={quote.customerName}
                  onChange={(e) => setQuote({ ...quote, customerName: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="E-post"
                  type="email"
                  value={quote.customerEmail}
                  onChange={(e) => setQuote({ ...quote, customerEmail: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Adress"
                  value={quote.customerAddress}
                  onChange={(e) => setQuote({ ...quote, customerAddress: e.target.value })}
                  margin="normal"
                  required
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Postnummer"
                      value={quote.customerPostalCode}
                      onChange={(e) => setQuote({ ...quote, customerPostalCode: e.target.value })}
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Ort"
                      value={quote.customerCity}
                      onChange={(e) => setQuote({ ...quote, customerCity: e.target.value })}
                      margin="normal"
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <QuoteItems items={quote.items} onChange={handleItemsChange} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Offertinformation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Giltig till"
                      value={quote.validUntil}
                      onChange={(date) => setQuote({ ...quote, validUntil: date || new Date() })}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                  </Grid>
                </Grid>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={quote.status}
                    onChange={(e) => setQuote({ ...quote, status: e.target.value as Quote['status'] })}
                    label="Status"
                  >
                    <MenuItem value="pending">Väntande</MenuItem>
                    <MenuItem value="accepted">Accepterad</MenuItem>
                    <MenuItem value="rejected">Avvisad</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={handlePreview}
            disabled={loading || !quote.customerName || !quote.customerEmail || !quote.customerAddress || !quote.customerPostalCode || !quote.customerCity}
          >
            Förhandsgranska
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading || !quote.customerName || !quote.customerEmail || !quote.customerAddress || !quote.customerPostalCode || !quote.customerCity}
          >
            Skapa offert
          </Button>
        </Box>

        <Dialog
          open={previewOpen}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { minHeight: '80vh' }
          }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>Förhandsgranskning av offert</Typography>
              <Box>
                <IconButton
                  onClick={() => {
                    if (previewUrl) {
                      const link = document.createElement('a');
                      link.href = previewUrl;
                      link.download = `offert-${quote.customerName}.pdf`;
                      link.click();
                    }
                  }}
                  title="Ladda ner PDF"
                >
                  <DownloadIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    const iframe = document.querySelector('iframe[name="pdf-preview"]') as HTMLIFrameElement;
                    if (iframe?.contentWindow) {
                      iframe.contentWindow.print();
                    }
                  }}
                  title="Skriv ut"
                >
                  <PrintIcon />
                </IconButton>
                <IconButton onClick={handleClosePreview} title="Stäng">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {previewUrl && (
              <Box sx={{ width: '100%', height: 'calc(80vh - 100px)' }}>
                <object
                  data={previewUrl}
                  type="application/pdf"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                >
                  <p>Din webbläsare kan inte visa PDF:er direkt. Klicka på nedladdningsknappen för att öppna filen.</p>
                </object>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CreateQuote; 